import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { fetchShipmentsForScanning, fetchDrivers } from '../utils/api';
import { toast } from 'react-toastify';

export default function ScanningScreen() {
  const navigate = useNavigate();
  const location = useLocation();

  // ------------------
  // 1. State for Date
  // ------------------
  // Start with an empty date instead of default to today
  const [selectedDate, setSelectedDate] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Load date from URL parameter on initial render
  useEffect(() => {
    // Get date from URL parameters (e.g., ?date=2023-11-15)
    const queryParams = new URLSearchParams(location.search);
    const dateParam = queryParams.get('date');
    
    if (dateParam) {
      setSelectedDate(dateParam);
    }
  }, [location.search]);

  // Update URL when date changes
  useEffect(() => {
    if (selectedDate) {
      // Update URL without full page reload
      const queryParams = new URLSearchParams(location.search);
      queryParams.set('date', selectedDate);
      navigate(`${location.pathname}?${queryParams.toString()}`, { replace: true });
    }
  }, [selectedDate, navigate, location.pathname, location.search]);

  // ----------------------------
  // 2. State for Barcode Groups
  // ----------------------------
  // Holds shipments grouped by driver. Example:
  // { driver_1: ["SH ...", "SH ..."], driver_2: [...], ... }
  const [shipmentsByDriver, setShipmentsByDriver] = useState({});

  // For quick lookup: { "SH ...": "driver_1", ... }
  const [deliveryToDriverMap, setDeliveryToDriverMap] = useState({});
  
  // New state for shipment types: { "SH...": "Delivery", "SH...": "PickUp" }
  const [shipmentTypes, setShipmentTypes] = useState({});
  
  // State for drivers data
  const [driversData, setDriversData] = useState({});

  // ------------------------
  // 3. State for Scanning
  // ------------------------
  const [barcode, setBarcode] = useState('');
  const [pendingScan, setPendingScan] = useState(null);

  // ---------------------
  // 4. Fetch Drivers Data
  // ---------------------
  useEffect(() => {
    const loadDriversData = async () => {
      try {
        const drivers = await fetchDrivers();
        
        // Create a map of driver_id to driver details
        const driverMap = {};
        const ids = [];
        
        drivers.forEach(driver => {
          driverMap[driver.id] = driver;
          ids.push(driver.id);
        });
        
        setDriversData(driverMap);
        
        console.log('Loaded drivers data:', driverMap);
      } catch (error) {
        console.error('Error loading drivers:', error);
        toast.error('Failed to load drivers data');
      }
    };
    
    loadDriversData();
  }, []);

  // ---------------------
  // 5. Fetch on Date Selection
  // ---------------------
  useEffect(() => {
    // Only fetch if we have a valid date
    if (!selectedDate) return;

    const fetchShipments = async () => {
      setIsLoading(true);
      try {
        // Call our new API function
        const data = await fetchShipmentsForScanning(selectedDate);
        console.log('Shipments data for scanning:', data);
        
        // Filter out PickUp shipments and only keep Delivery ones
        const filteredShipmentsByDriver = {};
        const filteredDeliveryToDriverMap = {};
        
        // Process shipment types
        setShipmentTypes(data.shipmentTypes || {});
        
        // Filter shipments by driver to only include Delivery types
        Object.keys(data.shipmentsByDriver || {}).forEach(driverId => {
          const driverShipments = data.shipmentsByDriver[driverId] || [];
          // Only keep shipments that are of type 'Delivery' or null/undefined (assume Delivery)
          const deliveryShipments = driverShipments.filter(shipmentName => {
            const type = data.shipmentTypes?.[shipmentName] || 'Delivery';
            return type !== 'PickUp';
          });
          
          if (deliveryShipments.length > 0) {
            filteredShipmentsByDriver[driverId] = deliveryShipments;
          }
        });
        
        // Filter the lookup map to only include Delivery shipments
        Object.keys(data.deliveryToDriverMap || {}).forEach(shipmentName => {
          const type = data.shipmentTypes?.[shipmentName] || 'Delivery';
          if (type !== 'PickUp') {
            filteredDeliveryToDriverMap[shipmentName] = data.deliveryToDriverMap[shipmentName];
          }
        });
        
        // Store the filtered data in state
        setShipmentsByDriver(filteredShipmentsByDriver);
        setDeliveryToDriverMap(filteredDeliveryToDriverMap);
        setPendingScan(null);
        
        console.log('Filtered delivery shipments:', {
          original: data.shipmentsByDriver,
          filtered: filteredShipmentsByDriver
        });
      } catch (error) {
        console.error('Error fetching shipments:', error);
        toast.error(error.message || 'Failed to load shipments for scanning');
        // Reset states if fetch fails
        setShipmentsByDriver({});
        setDeliveryToDriverMap({});
        setShipmentTypes({});
        setPendingScan(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchShipments();
  }, [selectedDate]);

  // ------------------------------------------------------
  // 6. Utility to Display & Label Drivers
  // ------------------------------------------------------
  /**
   * Get the driver display name from the driver ID
   * If driver data is available, use the driver's name
   * Otherwise, use a formatted driver ID
   */
  const getDriverDisplayName = (driverId) => {
    if (!driverId) return '';
    
    // If we have driver data, use the driver's name
    if (driversData[driverId]) {
      return driversData[driverId].name;
    }
    
    // Fallback to formatted driver ID if no driver data
    return formatDriverName(driverId);
  };
  
  /**
   * Get the driver's position number (1-based) within the active drivers for this date
   */
  const getDriverNumber = (driverId) => {
    if (!driverId) return '';
    
    // Get the list of active driver IDs for the selected date
    const activeDriverIds = Object.keys(shipmentsByDriver);
    const position = activeDriverIds.indexOf(driverId.toString()) + 1;
    
    return position > 0 ? position : '';
  };
  
  /**
   * If driver = "driver_1" => "Driver 1"
   * If driver = "Division Location" => "Division Pick Up"
   * etc.
   */
  const formatDriverName = (driver) => {
    if (!driver) return '';
    const lower = driver.toString().trim().toLowerCase();

    // 1) Check for "Division Location"
    if (lower === 'division location') {
      return 'Division Pick Up';
    }

    // 2) Fallback to "Driver X" if matching "driver_x"
    const match = lower.match(/driver_(\d+)/);
    if (match) {
      return `Driver ${match[1]}`;
    }

    // 3) If none matched, just return the raw driver string
    return driver;
  };

  /**
   * This is used for the voice label: "One", "Two", "Three", or "Division Pick Up", etc.
   */
  const getDriverLabel = (driver) => {
    if (!driver) return '';

    // Get the driver number based on position in active drivers
    const driverNumber = getDriverNumber(driver);
    
    // Convert driver number to word
    switch (driverNumber) {
      case 1: return 'One';
      case 2: return 'Two';
      case 3: return 'Three';
      case 4: return 'Four';
      case 5: return 'Five';
      default: 
        if (driverNumber) {
          return driverNumber.toString();
        }
        return '';
    }
  };

  // --------------------------------------------
  // 7. Speech: Speak "One"/"Two"/"Three", etc.
  // --------------------------------------------
  const speakMessage = (message) => {
    if ('speechSynthesis' in window) {
      const utterance = new SpeechSynthesisUtterance(message);
      window.speechSynthesis.speak(utterance);
    } else {
      console.warn('Speech Synthesis not supported in this browser.');
    }
  };

  // ------------------------------------
  // 8. Handle Barcode Input & Submission
  // ------------------------------------
  const handleBarcodeChange = (e) => {
    setBarcode(e.target.value);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleScanSubmit();
    }
  };

  const handleScanSubmit = () => {
    const trimmedBarcode = barcode.trim();
    if (!trimmedBarcode) return;

    // Check if the shipment is a PickUp - we only want to scan Deliveries
    const shipmentType = shipmentTypes[trimmedBarcode] || 'Delivery';
    if (shipmentType === 'PickUp') {
      // Don't process pickup shipments
      setPendingScan({
        code: trimmedBarcode,
        driver: null,
        label: 'Pickup shipment',
        isPickup: true
      });
      setBarcode('');
      return;
    }

    // 1) Finalize previous pending scan if any
    if (pendingScan && !pendingScan.isPickup) {
      removeScannedCodeFromDriver(pendingScan.code);
    }

    // 2) Check if newly scanned code matches a driver
    const matchedDriverId = deliveryToDriverMap[trimmedBarcode];

    if (!matchedDriverId) {
      // No matching driver => show "Unknown code"
      setPendingScan({
        code: trimmedBarcode,
        driver: null,
        label: 'Unknown code',
        isPickup: false
      });
    } else {
      // Get the driver's position number (1, 2, 3, etc.) in the active drivers list
      const driverNumber = getDriverNumber(matchedDriverId);
      
      // Get the driver's name for display
      const driverName = getDriverDisplayName(matchedDriverId);
      
      // Create a spoken message with both number and name
      let spokenMessage = driverName;
      if (driverNumber) {
        spokenMessage = `${driverNumber} - ${driverName}`;
      }
      
      // First remove this scanned code from the driver's list immediately
      removeScannedCodeFromDriver(trimmedBarcode);
      
      setPendingScan({
        code: trimmedBarcode,
        driver: matchedDriverId,
        driverNumber: driverNumber,
        label: driverName,
        isPickup: false
      });
      
      // Speak both driver number and name
      speakMessage(spokenMessage);
    }

    // Clear input
    setBarcode('');
  };

  // Remove scanned code from the driver's list
  const removeScannedCodeFromDriver = (codeToRemove) => {
    const driver = deliveryToDriverMap[codeToRemove];
    if (!driver) return;

    setShipmentsByDriver((prev) => {
      const newState = { ...prev };
      if (!newState[driver]) return newState;
      newState[driver] = newState[driver].filter((name) => name !== codeToRemove);
      return newState;
    });
  };

  // -------------------------------
  // 9. Rendering: Date, Stats, Etc.
  // -------------------------------
  // Build the driver stats
  const driverStats = Object.keys(shipmentsByDriver).map((driverId) => {
    // Keep only shipment numbers that start with "SH" (ignore others)
    const driverShipments = (shipmentsByDriver[driverId] || []).filter(code => code && code.startsWith('SH'));
    const count = driverShipments.length;
    const driverNumber = getDriverNumber(driverId);
    const driverName = getDriverDisplayName(driverId);
    
    return (
      <div key={driverId} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium text-gray-900">
              {driverNumber ? `Driver ${driverNumber} - ` : ''}{driverName}
            </h3>
            <p className="text-sm text-gray-600">Remaining deliveries: {count}</p>
          </div>
          <div className="text-2xl font-bold text-indigo-600">{count}</div>
        </div>
        {count > 0 && (
          <div className="mt-2">
            <p className="text-sm text-gray-500">Next deliveries:</p>
            <div className="mt-1 space-y-1">
              {driverShipments.map((code, index) => (
                <div key={index} className="text-sm font-mono text-gray-600">
                  {code}
                </div>
              ))}
              {/* Removed the "+more" line because we now display all shipments */}
            </div>
          </div>
        )}
      </div>
    );
  });

  // The last scan result
  let lastScanResult = null;
  if (pendingScan) {
    if (pendingScan.isPickup) {
      // Pickup shipment
      lastScanResult = (
        <div className="mt-6 p-4 rounded-lg bg-yellow-50 border border-yellow-200">
          <h3 className="text-lg font-medium mb-2">Last Scan Result:</h3>
          <div className="text-yellow-700">
            <p className="font-semibold">Pickup Shipment</p>
            <p>
              Code <span className="font-mono">{pendingScan.code}</span> is a pickup shipment.
              Pickups are not scanned in this interface.
            </p>
          </div>
        </div>
      );
    } else if (!pendingScan.driver) {
      // Unknown code
      lastScanResult = (
        <div className="mt-6 p-4 rounded-lg bg-red-50 border border-red-200">
          <h3 className="text-lg font-medium mb-2">Last Scan Result:</h3>
          <div className="text-red-700">
            <p className="font-semibold">Unknown Code</p>
            <p>
              Code <span className="font-mono">{pendingScan.code}</span> not found in
              list!
            </p>
          </div>
        </div>
      );
    } else {
      // Known driver
      const driverNumber = getDriverNumber(pendingScan.driver);
      const driverName = getDriverDisplayName(pendingScan.driver);
      
      lastScanResult = (
        <div className="mt-6 p-4 rounded-lg bg-green-50 border border-green-200">
          <h3 className="text-lg font-medium mb-2">Last Scan Result:</h3>
          <div className="text-green-700">
            <p className="font-semibold">Successfully Scanned</p>
            <p>
              Code: <span className="font-mono">{pendingScan.code}</span>
            </p>
            <p>
              Driver:{' '}
              <span className="font-semibold">
                {driverNumber ? `Driver ${driverNumber} - ` : ''}{driverName}
              </span>
            </p>
            <p>
              Label: <span className="font-semibold">{pendingScan.label}</span>
            </p>
          </div>
        </div>
      );
    }
  }

  const navigationButtons = (
    <button
      onClick={() => navigate(-1)}
      className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
    >
      Back
    </button>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <Navbar buttons={navigationButtons} />

      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Header Section */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Scanner Interface</h1>
            <p className="text-gray-600">
              Scan barcodes for the selected date to track deliveries per driver
            </p>
          </div>

          {/* Date Selector */}
          <div className="mb-6 flex items-center justify-center space-x-3">
            <label htmlFor="dateInput" className="text-gray-700 font-medium">
              Delivery Date:
            </label>
            <input
              id="dateInput"
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="border border-gray-300 rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          {/* Loading indicator */}
          {isLoading && (
            <div className="flex justify-center items-center my-8">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600"></div>
              <p className="ml-3 text-gray-600">Loading shipments...</p>
            </div>
          )}

          {/* Content only shown when date is selected and not loading */}
          {selectedDate && !isLoading && (
            /* Main Content Grid */
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Left Column - Scanner Input */}
              <div className="bg-white rounded-xl shadow-lg p-6">
                <div className="mb-6">
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">Scan Barcode</h2>
                  <div className="flex items-center space-x-3">
                    <input
                      type="text"
                      value={barcode}
                      onChange={handleBarcodeChange}
                      onKeyDown={handleKeyDown}
                      autoFocus
                      className="flex-1 border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      placeholder="Scan or type barcode here..."
                    />
                    <button
                      onClick={handleScanSubmit}
                      className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors duration-200"
                    >
                      Submit
                    </button>
                  </div>
                </div>

                {/* Last Scan Result */}
                {lastScanResult}
              </div>

              {/* Right Column - Driver Stats */}
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  Driver Statistics for {selectedDate}
                </h2>
                <div className="space-y-4">
                  {Object.keys(shipmentsByDriver).length === 0 ? (
                    <p className="text-gray-500">No deliveries loaded.</p>
                  ) : (
                    <>
                      {driverStats}
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Message when no date is selected */}
          {!selectedDate && !isLoading && (
            <div className="bg-white rounded-xl shadow-lg p-8 text-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <h2 className="text-xl font-semibold text-gray-800 mb-2">Select a Delivery Date</h2>
              <p className="text-gray-600">Choose a date to load shipments for scanning</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
